<script setup lang="ts">
import DashboardPanel from '@/modules/dashboard/components/DashboardPanel.vue';
import AgreementsTable from '@/modules/agreements/components/AgreementsTable.vue';
import AgreementView from '@/modules/agreements/components/AgreementView.vue';
import AgreementEditor from '@/modules/agreements/components/AgreementEditor.vue';
import { provide, shallowRef } from 'vue';
import {
  Agreement,
  ApproveAgreementRequest,
  ConnectResult,
  CreateAgreementRequest,
  CreateAgreementRequestSchema,
  Data,
  ModifyAgreementRequest,
  ModifyAgreementRequestSchema,
  RejectAgreementRequestSchema,
  agreementsService,
  create,
  isMessage,
} from '@/connect';
import { useDialogManager } from '@/plugins/dialog-manager';
import i18n from '@/localisation/i18n';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { Permission, useHasPermission } from '@/composables/user-permissions';

// for column state persistence
// @TODO: we should type provide/inject after we migrate all components to composition API
// (https://vuejs.org/guide/typescript/composition-api.html#typing-provide-inject)
provide('gridStateKey', 'AgreementsView');

const dialogManager = useDialogManager();
const snackbarManager = useSnackbarManager();

const agreementView = shallowRef<null | Data<Agreement>>(null);
const agreementSource = shallowRef<null | Data<Agreement>>(null);
const agreementRequest = shallowRef<
  null | Data<ModifyAgreementRequest> | Data<CreateAgreementRequest>
>(null);

function agreementNew() {
  agreementSource.value = null;
  agreementRequest.value = create(CreateAgreementRequestSchema);
}

function agreementEdit(agreement: Data<Agreement>) {
  agreementSource.value = agreement;
  agreementRequest.value = create(ModifyAgreementRequestSchema, agreement);
}

function agreementDuplicate(agreement: Data<Agreement>) {
  agreementSource.value = agreement;
  agreementRequest.value = create(CreateAgreementRequestSchema, agreement);
  agreementRequest.value.shortName = '';
}

function agreementClose(agreement: Data<Agreement>) {
  dialogManager.ask({
    title: 'Close Agreement',
    color: 'error',
    message: `Are you sure you want to close "${agreement.shortName}" agreement?`,
    acceptText: `Close the Agreement`,
    onAccept: async () => {
      const result = await agreementsService.cancelAgreement(agreement);
      handleConnectResult(result, `Closed "${agreement.shortName}" agreement`);
    },
    rejectText: i18n.t('cancelButton'),
  });
}

function agreementReject(agreement: Data<Agreement>, onRejected: () => void) {
  dialogManager.ask({
    title: 'Reject Agreement',
    color: 'error',
    message: `Are you sure you want to reject "${agreement.id}" agreement?`,
    acceptText: `Reject the Agreement`,
    onAccept: async () => {
      const result = await agreementsService.rejectAgreement(
        create(RejectAgreementRequestSchema, { id: agreement.id })
      );
      handleConnectResult(result, `Rejected "${agreement.id}" agreement`);
      onRejected();
    },
    rejectText: i18n.t('cancelButton'),
  });
}

async function agreementApprove(request: Data<ApproveAgreementRequest>) {
  const result = await agreementsService.approveAgreement(request);
  handleConnectResult(result, `Approved "${request.shortName}" agreement`);
}

async function agreementSave(
  request: Data<ModifyAgreementRequest> | Data<CreateAgreementRequest>
): Promise<void> {
  if (isMessage(request, ModifyAgreementRequestSchema)) {
    const result = await agreementsService.modifyAgreement(request);
    handleConnectResult(result, `Modified "${request.shortName}"`);
  }
  if (isMessage(request, CreateAgreementRequestSchema)) {
    const result = await agreementsService.createAgreement(request);
    handleConnectResult(result, `Created "${request.shortName}"`);
  }
}

function handleConnectResult(result: ConnectResult<unknown>, successMessage: string) {
  snackbarManager.show(
    result.success
      ? {
          message: successMessage,
          color: 'green',
        }
      : {
          message: i18n.t(result.error, result.details) as string,
          color: 'red',
        }
  );
}

const canCreateAgreement = useHasPermission(Permission.AGREEMENT_CREATE);
const canEditOrCreateAgreement = useHasPermission(
  Permission.AGREEMENT_CREATE,
  Permission.AGREEMENT_EDIT
);

const reservedShortNames = shallowRef(new Set<string>());
</script>

<template>
  <div class="wrapper d-flex flex-column fill-height">
    <DashboardPanel no-collapse title="Agreements">
      <div v-if="canCreateAgreement" class="d-flex gap-1 ma-4">
        <v-btn color="primary" @click="agreementNew">Add agreement</v-btn>
      </div>

      <AgreementView
        v-if="agreementView"
        :agreement="agreementView"
        :reserved-short-names="reservedShortNames"
        @approve="agreementApprove"
        @cancel="agreementView = null"
        @close="agreementClose"
        @duplicate="agreementDuplicate"
        @edit="agreementEdit"
        @reject="agreementReject"
      />
      <AgreementEditor
        v-if="canEditOrCreateAgreement"
        :reserved-short-names="reservedShortNames"
        :source="agreementSource"
        :value="agreementRequest"
        @cancel="agreementRequest = null"
        @save="agreementSave"
      />
      <AgreementsTable
        data-test="agreements-table"
        @close="agreementClose"
        @duplicate="agreementDuplicate"
        @edit="agreementEdit"
        @update:reserved-short-names="(value) => (reservedShortNames = value)"
        @view="(value) => (agreementView = value)"
      />
    </DashboardPanel>
  </div>
</template>

<style scoped lang="scss">
.wrapper {
  width: 100%;
  height: 100%;
}
</style>
